import { useNavigate } from "react-router-dom"
import { Icon } from "@/components/Icon"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { useCases } from "@/features/cases/hooks/useCases"
import { toCaseVM, TYPE_ICON, STATUS_PILL, type CaseVM } from "@/features/cases/case-view"
import { useEntitlements } from "@/features/billing/hooks/useEntitlements"
import { openPaywall } from "@/lib/quotaGate"
import { useState } from "react"

function greetingFor(date: Date): string {
  const h = date.getHours()
  if (h < 12) return "Good morning"
  if (h < 17) return "Good afternoon"
  return "Good evening"
}

export function HomeView() {
  const navigate = useNavigate()
  const { session } = useAuth()
  const { cases, setActiveCase } = useCases()
  const { entitlements, subscribed, metric, isLoading, error } = useEntitlements()
  const nira = metric("nira.message")
  const remainingTurns = nira && nira.limit !== null ? Math.max(0, nira.limit - nira.used) : 0

  const user = session?.user
  const firstName = (user?.displayName?.trim() || user?.email?.split("@")[0] || "Officer").split(
    /[\s._-]+/,
  )[0]
  const vms = cases.map((t) => toCaseVM(t, user))

  const awaiting = vms.filter((c) => c.status === "review").length
  const inProgress = vms.filter((c) => c.status === "progress").length
  const filed = vms.filter((c) => c.status === "filed").length
  const resume = vms.find((c) => c.status === "review") ?? vms.find((c) => c.status === "progress")

  const openCase = (id: string) => {
    const thread = cases.find((c) => c.id === id)
    if (thread) setActiveCase(thread)
    navigate("/drafting")
  }

  const subParts: string[] = []
  if (awaiting) subParts.push(`${awaiting} case${awaiting > 1 ? "s" : ""} awaiting your review`)
  if (inProgress) subParts.push(`${inProgress} draft${inProgress > 1 ? "s" : ""} in progress`)
  const sub =
    subParts.length > 0
      ? `You have ${subParts.join(" and ")}.`
      : "Upload a Show Cause Notice and reply to begin a new adjudication."

  const periodEnd = entitlements?.subscription?.current_end ?? null
  const periodLabel = periodEnd
    ? new Date(periodEnd).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : null

  return (
    <>
      <div className="topbar">
        <span className="topbar__crumb">{firstName}'s desk</span>
        <div className="topbar__spacer" />
        <button className="btn btn--primary btn--sm" onClick={() => navigate("/ask")}>
          <Icon name="plus" size={14} /> Open simulators
        </button>
      </div>

      <div className="home">
        <div className="home__inner">
          <div className="home__hero">
            <h1>
              {greetingFor(new Date())}, {firstName}
            </h1>
            <p>{sub}</p>
          </div>

          {resume && (
            <button className="home__resume" onClick={() => openCase(resume.id)}>
              <div>
                <div className="rz-eyebrow">Resume review</div>
                <div className="rz-title">{resume.title}</div>
                <div className="rz-meta mono">{resume.ref}</div>
                <div className="rz-note">
                  <Icon name="eye" size={14} /> {resume.statusLabel}
                </div>
              </div>
              <div className="rz-amt">
                <div className="v">{resume.amount}</div>
                <div className="l">demand</div>
              </div>
            </button>
          )}

          <div className="home__metrics">
            <Metric v={awaiting} l="Awaiting your review" />
            <Metric v={inProgress} l="Draft in progress" />
            <Metric v={cases.length} l="Open adjudications" />
            <Metric v={filed} l="Orders filed" />
          </div>

          <section className="mb-8 rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6" aria-label="Subscription and usage">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Subscription &amp; Usage</div>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold text-slate-900">NIRA</h2>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${subscribed ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>
                    {subscribed ? "Active" : "Free"}
                  </span>
                </div>
              </div>
              {!subscribed && entitlements && (
                <button type="button" className="btn btn--primary btn--sm" onClick={() => openPaywall(entitlements, "nira.message")}>
                  <Icon name="sparkle" size={14} /> Upgrade
                </button>
              )}
            </div>
            {isLoading ? (
              <p className="mt-4 text-sm text-slate-500">Loading research usage…</p>
            ) : error ? (
              <p className="mt-4 text-sm text-red-600" role="alert">Could not load subscription usage.</p>
            ) : (
              <div className="mt-4 flex flex-wrap items-end justify-between gap-3 border-t border-slate-100 pt-4">
                <div>
                  <div className="text-sm font-medium text-slate-800">{nira && nira.limit !== null ? `${remainingTurns} of ${nira.limit} research turns left ${nira.window === "day" ? "today" : nira.window === "month" ? "this month" : ""}` : "Research allowance for this account"}</div>
                  <div className="mt-1 text-xs text-slate-500">{periodLabel ? `Current period ends ${periodLabel}` : ""}</div>
                </div>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => navigate("/subscription")}>Manage subscription</button>
              </div>
            )}
          </section>

          <div className="home__section-label">Start something</div>
          <div className="home__actions">
            <button className="home-action" onClick={() => navigate("/ask")}>
              <div className="home-action__ico">
                <Icon name="gavel" size={19} />
              </div>
              <div>
                <div className="home-action__t">Open simulators</div>
                <div className="home-action__d">
                  Upload an SCN and reply; draft the OIO section by section.
                </div>
              </div>
              <span className="home-action__go">
                <Icon name="arrowr" size={16} />
              </span>
            </button>
            <button className="home-action" onClick={() => navigate("/vault")}>
              <div className="home-action__ico">
                <Icon name="archive" size={19} />
              </div>
              <div>
                <div className="home-action__t">Open the Vault</div>
                <div className="home-action__d">
                  Browse every case and document in your repository.
                </div>
              </div>
              <span className="home-action__go">
                <Icon name="arrowr" size={16} />
              </span>
            </button>
            <button className="home-action" onClick={() => navigate("/qa")}>
              <div className="home-action__ico">
                <Icon name="qa" size={19} />
              </div>
              <div>
                <div className="home-action__t">Ask a question</div>
                <div className="home-action__d">
                  Query GST law, circulars and precedent — fully cited.
                </div>
              </div>
              <span className="home-action__go">
                <Icon name="arrowr" size={16} />
              </span>
            </button>
          </div>

          <div className="home__section-label">Recent cases</div>
          <div className="home__recent">
            {vms.length === 0 ? (
              <div className="empty" style={{ height: "auto", padding: "40px 20px" }}>
                <div className="empty__ico">
                  <Icon name="folder" size={22} />
                </div>
                <h3>No cases yet</h3>
                <p>Start a new draft to create your first adjudication.</p>
              </div>
            ) : (
              <table className="vtable">
                <thead>
                  <tr>
                    <th style={{ width: "44%" }}>Workspace</th>
                    <th>Type</th>
                    <th>Demand</th>
                    <th>Status</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {vms.slice(0, 5).map((c) => (
                    <CaseRow key={c.id} c={c} onOpen={openCase} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

function Metric({ v, l }: { v: number; l: string }) {
  return (
    <div className="statcard">
      <div className="sv">{v}</div>
      <div className="sl">{l}</div>
    </div>
  )
}

function CaseRow({ c, onOpen }: { c: CaseVM; onOpen: (id: string) => void }) {
  return (
    <tr onClick={() => onOpen(c.id)}>
      <td>
        <div className="vt-title">{c.title}</div>
        <div className="vt-sub mono">{c.ref}</div>
      </td>
      <td>
        <span className="vt-type">
          <span className="ti">
            <Icon name={TYPE_ICON[c.type]} size={14} />
          </span>
          {c.typeLabel}
        </span>
      </td>
      <td className="vt-amt">{c.amount}</td>
      <td>
        <span className={"pill pill--" + STATUS_PILL[c.status]}>{c.statusLabel}</span>
      </td>
      <td className="mono" style={{ color: "var(--ink-3)", fontSize: 12 }}>
        {c.updated}
      </td>
    </tr>
  )
}
